import React, {
  useRef,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react';

import {
  KiteCard,
  KiteAlert,
  KiteModal,
  KiteIcon,
  KiteButton,
  KiteTooltip,
} from '@kite/react-kite';
import { ITCOForm, ITCOSubmission } from 'types';
import { ITcoLocations } from 'types/TcoProducts';
import {
  useTCORenderFields,
  useQueryData,
  usePostTcoMultipleLocations,
} from 'hooks';
import { useParams } from 'react-router-dom';
import './TCOLocations.scss';
import LocationCard from '../LocationCard';
import ManuallyAddLocations from '../AddLocationMethods/ManuallyAddLocations';
import UseLocationsFromSPT from '../AddLocationMethods/UseLocationsFromSPT';
import ApplyUnderlayByLocationGroups from '../AddLocationMethods/ApplyUnderlayByLocationGroups';
import ApplySameUnderlayToAll from '../AddLocationMethods/ApplySameUnderlayToAll';
import { useGetTcoLocations } from 'hooks/apiHooks/tcoLocations/useGetTcoLocations';
import { useDeleteTcoLocations } from 'hooks/apiHooks/tcoLocations/useDeleteTcoLocations';
import { useUpdateTcoSubmissions } from 'hooks/apiHooks/tco/useUpdateTcoSubmissions';
import { usePostTcoLocations } from 'hooks/apiHooks/tcoLocations/usePostTcoLocations';
import { useUpdateTcoLocations } from 'hooks/apiHooks/tcoLocations/useUpdateTcoLocations';
import { TCOSelect } from 'components';
import { ITCOField, ITCOFieldInput } from 'types';
import SpectrumDiyTotal from 'components/TCOComponents/TCOOverview/SpectrumDiyTotal';
import { tcoFormattedPrice } from 'utils/defaultsAndConstants/tcoConstants';
import { ReactComponent as RefreshIcon } from '../../../../images/reset.svg';
import { ReactComponent as Checkbox } from '../../../../images/checked.svg';
import {
  TERM_FIELD_ID,
  ADD_UNDERLAY_FIELD_ID,
  MANUAL_ADD_OPTION,
  USE_LOCATIONS_FROM_SPT,
  APPLY_UNDERLAY_BY_LOCATION_GROUPS,
  APPLY_SAME_UNDERLAY_TO_ALL,
} from 'utils/defaultsAndConstants/constants';
import { useRefreshTcoUnderlaySelections } from 'hooks/useRefreshTcoUnderlaySelections';

export interface UnderlayFormData {
  id?: string;
  underlayId: string;
  spectrumPrice: string;
  diyPrice: string;
  term?: string;
}
export interface ITCOLocationsProps {
  form: ITCOForm;
  tcoSubmissions: ITCOSubmission[];
}

const TCOLocations = ({ form, tcoSubmissions }: ITCOLocationsProps) => {
  const { name, fields, id } = form;
  const formRef = useRef<HTMLFormElement>(null);
  const { estimateId = '' } = useParams();
  const [showModal, setShowModal] = useState(false);
  const prevSelectionRef = useRef<string | null>(null);
  const termValue = useMemo(() => {
    const termSubmission = tcoSubmissions.find(
      (sub) => sub.fieldId === TERM_FIELD_ID
    );
    return termSubmission?.answer || '1';
  }, [tcoSubmissions]);
  const { data: tcoLocations = [], isLoading: locationsLoading } =
    useGetTcoLocations({
      params: { estimateId },
    });
  const { userId, currentEstimate } = useQueryData();
  const locationsToImport = useMemo(
    () => currentEstimate?.locations ?? [],
    [currentEstimate]
  );
  const { deleteTcoLocation } = useDeleteTcoLocations(estimateId);
  const { updateTcoSubmissions } = useUpdateTcoSubmissions({ estimateId });
  const { postTcoLocation } = usePostTcoLocations(estimateId);
  const { postTcoMultipleLocation } = usePostTcoMultipleLocations(estimateId);
  const { updateTcoLocations } = useUpdateTcoLocations();
  const { refreshUnderlaySelections } = useRefreshTcoUnderlaySelections();
  const [locationToEdit, setLocationToEdit] = useState<ITcoLocations | null>(
    null
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [locationToDelete, setLocationToDelete] =
    useState<ITcoLocations | null>(null);
  const [showChangeSelectionModal, setShowChangeSelectionModal] =
    useState(false);
  const [addUnderlaySelection, setAddUnderlaySelection] = useState<
    string | null
  >(null);
  const [lastSavedUnderlaySelection, setLastSavedUnderlaySelection] = useState<
    string | null
  >(null);
  const [pendingUnderlaySelection, setPendingUnderlaySelection] = useState<
    string | null
  >(null);
  const [resetCounter, setResetCounter] = useState(0);
  const addUnderlayFieldConfig = useMemo(
    () => fields.find((f) => f.id === ADD_UNDERLAY_FIELD_ID),
    [fields]
  );
  const otherFields = useMemo(
    () => fields.filter((f) => f.id !== ADD_UNDERLAY_FIELD_ID),
    [fields]
  );

  const [underlayForms, setUnderlayForms] = useState<UnderlayFormData[]>([]);
  // const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  useEffect(() => {
    const initialSubmission = tcoSubmissions.find(
      (sub) => sub.fieldId === ADD_UNDERLAY_FIELD_ID
    )?.answer;
    setAddUnderlaySelection(initialSubmission ?? null);
    setLastSavedUnderlaySelection(initialSubmission ?? null);
  }, [tcoSubmissions]);
  const { fieldComponents, updateTcoSubmissionsError } = useTCORenderFields({
    fields: otherFields,
    tcoSubmissions,
    formId: id,
  });
  const [showResetModal, setShowResetModal] = useState(false);
  const toggleResetModal = useCallback(() => {
    setShowResetModal((v) => !v);
  }, []);

  const handleDeleteAll = useCallback(() => {
    tcoLocations.forEach((loc) => deleteTcoLocation(loc));
    setShowResetModal(false);
    setLocationToEdit(null);
  }, [tcoLocations, deleteTcoLocation]);

  const handleAddLocation = useCallback(
    (location: ITcoLocations) => {
      postTcoLocation({
        name: location.name,
        noOfSites: location.noOfSites || 1,
        estimateId,
        updatedBy: userId,
        underlaySelections: location.underlaySelections || [],
      });
    },
    [postTcoLocation, estimateId, userId]
  );
  const handleEditLocation = useCallback((location: ITcoLocations) => {
    setLocationToEdit(location);
  }, []);
  const handleUpdateLocation = useCallback(
    (location: ITcoLocations, originalLocation: ITcoLocations) => {
      updateTcoLocations({
        id: originalLocation.id,
        estimateId,
        name: location.name,
        noOfSites: location.noOfSites || originalLocation.noOfSites,
        underlaySelections: location.underlaySelections || [],
        updatedBy: userId,
      });
    },
    [updateTcoLocations, estimateId, userId]
  );

  const handleCancelEdit = useCallback(() => {
    setLocationToEdit(null);
  }, []);

  // const handleSaveLocation = useCallback(() => {
  //   const formatPrice = (price: string): number => {
  //     const num = parseFloat(price);
  //     return isNaN(num) ? 0 : parseFloat(num.toFixed(2));
  //   };
  //   if (LengthOfLocations) {
  //     locationsToImport.forEach((loc) => {
  //       const newLocation: ITcoLocations = {
  //         id: '',
  //         name: `${loc.name}`,
  //         noOfSites: 1,
  //         estimateId,
  //         updatedBy: userId,
  //         underlaySelections: underlayForms.map((form) => ({
  //           id: '',
  //           locationId: '',
  //           underlayId: form.underlayId || '',
  //           term: termValue,
  //           spectrumPrice: formatPrice(form.spectrumPrice),
  //           diyPrice: formatPrice(form.diyPrice),
  //           updatedBy: userId,
  //           createdAt: '',
  //           updatedAt: '',
  //         })),
  //         createdAt: '',
  //         updatedAt: '',
  //       };
  //       handleAddLocation(newLocation);
  //     });
  //   }
  //   setShowModal(false);
  // }, [
  //   underlayForms,
  //   userId,
  //   estimateId,
  //   termValue,
  //   locationsToImport,
  //   LengthOfLocations,
  //   handleAddLocation,
  // ]);

  const handleSaveLocation = useCallback(() => {
    if (!locationsToImport.length) {
      setShowModal(false);
      return;
    }
    const batch: Partial<ITcoLocations>[] = locationsToImport.map((loc) => ({
      name: loc.name,
      noOfSites: 1,
      estimateId,
      updatedBy: userId,
      underlaySelections: [],
    }));
    postTcoMultipleLocation(batch);
    setShowModal(false);
  }, [locationsToImport, estimateId, userId, postTcoMultipleLocation]);

  const handleAddUnderlayLocation = useCallback(() => {
    const formatPrice = (price: string): number => {
      const num = parseFloat(price);
      return isNaN(num) ? 0 : parseFloat(num.toFixed(2));
    };
    const newLocation: ITcoLocations = {
      id: '',
      name: 'Apply Same Underlay to All',
      noOfSites: 1,
      estimateId,
      updatedBy: userId,
      underlaySelections: underlayForms.map((form) => ({
        id: '',
        locationId: '',
        underlayId: form.underlayId || '',
        term: termValue,
        spectrumPrice: formatPrice(form.spectrumPrice),
        diyPrice: formatPrice(form.diyPrice),
        updatedBy: userId,
        createdAt: '',
        updatedAt: '',
      })),
      createdAt: '',
      updatedAt: '',
    };
    handleAddLocation(newLocation);
  }, [underlayForms, userId, estimateId, termValue, handleAddLocation]);

  const toggleModal = useCallback(() => {
    if (tcoLocations.length === 0) {
      setShowModal((prev) => !prev);
      if (!showModal) {
        setUnderlayForms([]);
      }
    }
  }, [showModal, tcoLocations]);

  const handleDeleteLocation = useCallback((location: ITcoLocations) => {
    setLocationToDelete(location);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(() => {
    if (locationToDelete) {
      deleteTcoLocation(locationToDelete);
    }
    setShowDeleteModal(false);
    setLocationToDelete(null);
  }, [deleteTcoLocation, locationToDelete]);
  const cancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setLocationToDelete(null);
  }, []);
  const handleUnderlaySelectionChange = useCallback(
    (fieldId: string, newValue: string | null) => {
      if (tcoLocations.length > 0 && newValue !== lastSavedUnderlaySelection) {
        setPendingUnderlaySelection(newValue);
        setShowChangeSelectionModal(true);
      } else {
        setAddUnderlaySelection(newValue);
        setLastSavedUnderlaySelection(newValue);
        const existingSubmission = tcoSubmissions.find(
          (sub) => sub.fieldId === ADD_UNDERLAY_FIELD_ID
        );
        const submissionToUpdate: Partial<ITCOSubmission> = existingSubmission
          ? {
              ...existingSubmission,
              formId: undefined,
              answer: newValue,
              updatedBy: userId,
            }
          : {
              estimateId,
              fieldId: ADD_UNDERLAY_FIELD_ID,
              answer: newValue,
              updatedBy: userId,
            };
        updateTcoSubmissions([submissionToUpdate]);
      }
    },
    [
      tcoLocations.length,
      lastSavedUnderlaySelection,
      updateTcoSubmissions,
      tcoSubmissions,
      estimateId,
      userId,
    ]
  );
  const confirmChangeSelection = useCallback(() => {
    if (!pendingUnderlaySelection) return;
    tcoLocations.forEach((location) => {
      deleteTcoLocation(location);
    });
    const newValue = pendingUnderlaySelection;
    setAddUnderlaySelection(newValue);
    setLastSavedUnderlaySelection(newValue);
    const existingSubmission = tcoSubmissions.find(
      (sub) => sub.fieldId === ADD_UNDERLAY_FIELD_ID
    );
    const submissionToUpdate: Partial<ITCOSubmission> = existingSubmission
      ? {
          ...existingSubmission,
          formId: undefined,
          answer: newValue,
          updatedBy: userId,
        }
      : {
          estimateId,
          fieldId: ADD_UNDERLAY_FIELD_ID,
          answer: newValue,
          updatedBy: userId,
        };
    updateTcoSubmissions([submissionToUpdate]);
    setShowChangeSelectionModal(false);
    setLocationToEdit(null);
    setPendingUnderlaySelection(null);
    setAddUnderlaySelection(lastSavedUnderlaySelection);
    setResetCounter((c) => c + 1);
  }, [
    pendingUnderlaySelection,
    tcoLocations,
    deleteTcoLocation,
    updateTcoSubmissions,
    tcoSubmissions,
    estimateId,
    userId,
    lastSavedUnderlaySelection,
  ]);

  const cancelChangeSelection = useCallback(() => {
    setShowChangeSelectionModal(false);
    setPendingUnderlaySelection(null);
    setAddUnderlaySelection(lastSavedUnderlaySelection);
    setResetCounter((c) => c + 1);
  }, [lastSavedUnderlaySelection]);

  const renderAddLocationMethod = useMemo(() => {
    const selectedOption = addUnderlaySelection;

    if (selectedOption === MANUAL_ADD_OPTION) {
      return (
        <ManuallyAddLocations
          onAddLocation={handleAddLocation}
          onEditLocation={handleUpdateLocation}
          locationToEdit={locationToEdit}
          onCancelEdit={handleCancelEdit}
          term={termValue}
        />
      );
    }

    if (selectedOption === USE_LOCATIONS_FROM_SPT) {
      return (
        <UseLocationsFromSPT
          onEditLocation={handleUpdateLocation}
          locationToEdit={locationToEdit}
          onCancelEdit={handleCancelEdit}
          term={termValue}
        />
      );
    }

    if (selectedOption === APPLY_UNDERLAY_BY_LOCATION_GROUPS) {
      return (
        <ApplyUnderlayByLocationGroups
          onAddLocation={handleAddLocation}
          onEditLocation={handleUpdateLocation}
          locationToEdit={locationToEdit}
          onCancelEdit={handleCancelEdit}
          term={termValue}
        />
      );
    }

    if (selectedOption === APPLY_SAME_UNDERLAY_TO_ALL) {
      return (
        <ApplySameUnderlayToAll
          onEditLocation={handleUpdateLocation}
          term={termValue}
        />
      );
    }

    return null;
  }, [
    addUnderlaySelection,
    handleAddLocation,
    handleUpdateLocation,
    locationToEdit,
    handleCancelEdit,
    termValue,
  ]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
  }, []);

  const renderLocationCards = useCallback(() => {
    if (
      addUnderlaySelection === MANUAL_ADD_OPTION ||
      addUnderlaySelection === APPLY_UNDERLAY_BY_LOCATION_GROUPS ||
      addUnderlaySelection === USE_LOCATIONS_FROM_SPT
    ) {
      return tcoLocations.map((location) => (
        <LocationCard
          key={location.id}
          location={location}
          onEdit={handleEditLocation}
          onDelete={handleDeleteLocation}
          showLocationCount={
            addUnderlaySelection === APPLY_UNDERLAY_BY_LOCATION_GROUPS
          }
        />
      ));
    }

    return null;
  }, [
    tcoLocations,
    handleEditLocation,
    handleDeleteLocation,
    addUnderlaySelection,
  ]);

  const renderLocationsForSelectedMethod = useCallback(() => {
    switch (addUnderlaySelection) {
      case MANUAL_ADD_OPTION:
      case APPLY_UNDERLAY_BY_LOCATION_GROUPS:
      case USE_LOCATIONS_FROM_SPT:
        return (
          <div className="locations__cards-list">{renderLocationCards()}</div>
        );
      default:
        return null;
    }
  }, [addUnderlaySelection, renderLocationCards]);
  const showContent = useMemo(
    () => !!addUnderlaySelection,
    [addUnderlaySelection]
  );

  const renderAddUnderlayField = () => {
    if (!addUnderlayFieldConfig) return null;
    const fieldInput: ITCOFieldInput = {
      ...(addUnderlayFieldConfig as ITCOField),
      value: addUnderlaySelection,
    };
    const showSuggestionTooltip =
      addUnderlaySelection === APPLY_UNDERLAY_BY_LOCATION_GROUPS ||
      addUnderlaySelection === APPLY_SAME_UNDERLAY_TO_ALL;
    return (
      <div key={addUnderlayFieldConfig.id} className="tco-field">
        <div className="tco-field__label">
          <div className="tco-field__label-question">
            <span
              dangerouslySetInnerHTML={{ __html: addUnderlayFieldConfig.label }}
            ></span>
          </div>
        </div>
        <div className="tco-field-input">
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            <TCOSelect
              key={`select-${addUnderlaySelection}-${resetCounter}`}
              fieldInput={fieldInput}
              onFieldChange={handleUnderlaySelectionChange}
            />
            {showSuggestionTooltip && (
              <div className="location-suggestion-tooltip">
                <span className="suggestion-text">
                  Suggested for 20+ Locations
                </span>
              </div>
            )}
            {addUnderlaySelection === USE_LOCATIONS_FROM_SPT && (
              <div className="locations__reset-button-container">
                <div className="Checkbox_Button">
                  {tcoLocations.length > 0 ? (
                    <Checkbox className="checkbox-btn__icon" />
                  ) : (
                    <KiteButton type="primary" onClick={toggleModal}>
                      Import from SPT
                    </KiteButton>
                  )}
                </div>
                {tcoLocations.length > 0 && (
                  <div className="resetButton_Name">
                    <KiteButton
                      type="standalone-link"
                      onClick={toggleResetModal}
                    >
                      <RefreshIcon className="reset-btn__icon" />
                      <span>Reset</span>
                    </KiteButton>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
  const { totalSpectrumCost, totalDiyCost } = useMemo(() => {
    if (addUnderlaySelection === APPLY_SAME_UNDERLAY_TO_ALL) {
      if (tcoLocations.length === 0) {
        return { totalSpectrumCost: 0, totalDiyCost: 0 };
      }
      const latest = [...tcoLocations].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )[0];

      const spectrumTotal = latest.underlaySelections.reduce(
        (sum, sel) => sum + sel.spectrumPrice * latest.noOfSites,
        0
      );
      const diyTotal = latest.underlaySelections.reduce(
        (sum, sel) => sum + sel.diyPrice * latest.noOfSites,
        0
      );
      return { totalSpectrumCost: spectrumTotal, totalDiyCost: diyTotal };
    }
    let spectrumTotal = 0;
    let diyTotal = 0;
    tcoLocations.forEach((loc) =>
      loc.underlaySelections.forEach((sel) => {
        spectrumTotal += sel.spectrumPrice * loc.noOfSites;
        diyTotal += sel.diyPrice * loc.noOfSites;
      })
    );
    return { totalSpectrumCost: spectrumTotal, totalDiyCost: diyTotal };
  }, [tcoLocations, addUnderlaySelection]);

  useEffect(() => {
    const prev = prevSelectionRef.current;
    const now = addUnderlaySelection;
    if (
      now === APPLY_SAME_UNDERLAY_TO_ALL &&
      prev !== APPLY_SAME_UNDERLAY_TO_ALL &&
      !locationsLoading &&
      tcoLocations.length === 0
    ) {
      handleAddUnderlayLocation();
    }
    prevSelectionRef.current = now;
  }, [
    addUnderlaySelection,
    locationsLoading,
    tcoLocations.length,
    handleAddUnderlayLocation,
  ]);

  return (
    <>
      <KiteCard className="tco-locations">
        <div className="tco-locations-title-container">
          <h2>Locations</h2>
          <div className="refresh-button">
            <KiteButton
              type="standalone-link"
              size="small"
              onClick={() =>
                refreshUnderlaySelections({
                  tcoSubmissions,
                  tcoLocations,
                  refresh: true,
                })
              }
            >
              <KiteIcon name="refresh" size="22px" margin="0 0 0 0" />
              Refresh
            </KiteButton>
            <KiteTooltip>
              Clicking on refresh button will reset the underlay selections
              prices to original values.
            </KiteTooltip>
          </div>
        </div>
        <div className="locations-form">
          {updateTcoSubmissionsError && (
            <KiteAlert
              type="alert"
              title="Save Error"
              description="Something went wrong saving your answers. Please try again."
            />
          )}
          <form ref={formRef} onSubmit={handleSubmit}>
            <div className="locations__row">
              {renderAddUnderlayField()}
              {fieldComponents}
            </div>
            {showContent && (
              <>
                {renderLocationsForSelectedMethod()}
                {renderAddLocationMethod}
                {addUnderlaySelection === USE_LOCATIONS_FROM_SPT}
                <div className="implementation-costs__totals">
                  <SpectrumDiyTotal
                    title="Spectrum Total"
                    primaryCostLabel="Total Circuit Cost"
                    primaryCost={`$${tcoFormattedPrice(totalSpectrumCost)}`}
                  />
                  <SpectrumDiyTotal
                    title="DIY Total"
                    primaryCostLabel="Total Circuit Cost"
                    primaryCost={`$${tcoFormattedPrice(totalDiyCost)}`}
                    isDiy={true}
                  />
                </div>
              </>
            )}
          </form>
        </div>
      </KiteCard>

      <KiteModal
        canShow={showDeleteModal}
        onHide={cancelDelete}
        ctaCopy="Remove"
        ctaAction={confirmDelete}
        secondaryCtaCopy="Cancel"
        secondaryCtaAction={cancelDelete}
        className="delete-location-modal"
        disableBackgroundClose={true}
      >
        <div className="delete-modal__content">
          <div className="delete-modal__icon-title">
            <KiteIcon name="caution-circle-f" className="delete-modal__icon" />
            <h3>Remove Location?</h3>
          </div>
          <p>Are you sure you want to remove this location?</p>
          <p className="delete-modal__subtitle">
            This location will only be removed from this page, not SPT.
          </p>
        </div>
      </KiteModal>

      <KiteModal
        canShow={showChangeSelectionModal}
        onHide={cancelChangeSelection}
        ctaCopy="Remove"
        ctaAction={confirmChangeSelection}
        secondaryCtaCopy="Cancel"
        secondaryCtaAction={cancelChangeSelection}
        className="delete-location-modal"
        disableBackgroundClose={true}
      >
        <div className="delete-modal__content">
          <div className="delete-modal__icon-title">
            <KiteIcon name="caution-circle-f" className="delete-modal__icon" />
            <h3>Change How Underlays Are Added?</h3>
          </div>
          <p>
            If you change this selection, any previous underlays and location
            information you've added will be deleted
          </p>
        </div>
      </KiteModal>
      <div className="import__button-container">
        <KiteModal
          title={`Import ${locationsToImport.length} Locations from SPT`}
          canShow={showModal}
          onHide={toggleModal}
          ctaCopy={`Import All ${locationsToImport.length} Locations`}
          ctaAction={handleSaveLocation}
          secondaryCtaCopy="Cancel"
          secondaryCtaAction={toggleModal}
          className="location-modal-title"
        >
          <p>
            There are {locationsToImport.length} locations in SPT for this
            opportunity. Please note: This option is only recommended if there
            are fewer than 20 locations in SPT.
          </p>
          <p>Do you want to add them all?</p>
        </KiteModal>
      </div>
      <div className="resetContent_class">
        <KiteModal
          canShow={showResetModal}
          onHide={toggleResetModal}
          ctaCopy="Reset"
          ctaAction={handleDeleteAll}
          secondaryCtaCopy="Cancel"
          secondaryCtaAction={toggleResetModal}
          className="delete-location-modal"
        >
          <div className="delete-modal__content">
            <div className="delete-modal__icon-title">
              <KiteIcon
                name="caution-circle-f"
                className="delete-modal__icon"
              />
              <h3>Reset Locations?</h3>
            </div>
            <p>
              If you reset your locations, all of your locations will be removed
              from this page. This will not remove your locations from SPT.
            </p>
            <p>
              Any previous underlay and location information you've added will
              be deleted, even if you import the locations again from SPT.
            </p>
          </div>
        </KiteModal>
      </div>
    </>
  );
};

export default TCOLocations;
